import React, { FC, ReactElement, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation as UrlLocation, Navigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { clsx } from 'clsx';
// Components
import Text from '../text';
import DrawerMenu from '../appBar/drawerMenu';
import DrawerAdminMenu from '../appBar/drawerAdminMenu';
// Modules
import { actions as adminActions, selectors as adminSelectors } from '../../core/modules/admin';
// Queries
import { useGetContractInfos } from '../../core/services/contract';
import { useGetPlan } from '../../core/services/policies';
// Resources
import { paths } from '../../navigation/routes/paths';
import { ContractInfos, Plan } from '../../core/apiTypes';
import { getDefaultContractID, isUserAdmin } from '../../utils/user';
import { DEFAULT_TOP_BAR_HEIGHT, NavSideBarWidth, NavTopBarHeight } from '../../utils/constants';
import { useBreakpoints, useSpecificSize, WebLocation } from '../../customHooks/useBreakpoints';
import { KEY_WV_STATUS_BAR, asyncStorageApi, getBottomBarHeight, getTopBarHeight } from '../../localStorageAPI';
import { isWebView } from '../../utils/webview';
import useGlobalStyles from '../hook/useGlobalStyles';

const ContentPaddingTop = 52;

interface StyleProps {
    statusBarHeight: number;
    bottomBarHeight: number;
}

interface ContentContainerProps {
    children: React.ReactNode;
    title?: string;
    action?: ReactElement;
    actionLeft?: ReactElement;
}

interface StateProps {
    contract: ContractInfos;
    contractPlan: Plan;
}

interface DispatchProps {
    setContract: (data: ContractInfos) => void;
    setContractPlan: (data: Plan) => void;
}

type Props = ContentContainerProps & StateProps & DispatchProps;

const ContentContainer: FC<Props> = ({
    title,
    action,
    actionLeft,
    children,
    contract,
    contractPlan,
    setContract,
    setContractPlan,
}): ReactElement => {
    // Zero outside the Android WebView, where the native side or the OS already reserves the space.
    const bottomBarHeight = getBottomBarHeight();
    const classes = useStyles({
        statusBarHeight: getTopBarHeight() || DEFAULT_TOP_BAR_HEIGHT,
        bottomBarHeight,
    });
    const globalClasses = useGlobalStyles();

    const { aboveAll, isTabletSize, isMobileSize } = useBreakpoints(WebLocation.FLOW);

    const { pathname } = UrlLocation();
    const isAdmin = pathname.startsWith(paths.admin);
    const noNeedPlan = [
        // paths.adminBilling,
        paths.adminPayment,
        paths.adminEditPayment,
        paths.adminPaymentAdyenNew,
    ].includes(pathname);
    const shouldReduceMargin = useSpecificSize('1150px');

    useEffect(() => {
        // to remove cache using same scroll between page like reservation/id and list of all reservations
        window.scrollTo(0, 0);
    }, [pathname]);

    // Queries
    useGetPlan<Plan>({
        enabled: isAdmin && !noNeedPlan && !!contract?.plan?.uuid && contractPlan?.entity_id !== contract?.plan?.uuid,
        variables: {
            uuid: contract?.plan?.uuid,
        },
        onSuccess: (data: Plan) => {
            setContractPlan(data);
        },
    });

    useGetContractInfos<ContractInfos>({
        enabled: isAdmin && !noNeedPlan,
        variables: {
            id: getDefaultContractID(),
        },
        onSuccess: (data: ContractInfos) => {
            setContract(data);
        },
    });

    const isWebview = isWebView();

    const [statusBarHandling, setStatusBarHandling] = useState(asyncStorageApi.restoreDataNoLink(KEY_WV_STATUS_BAR));

    React.useEffect(() => {
        const shouldHandleStatusBarMount = asyncStorageApi.restoreDataNoLink(KEY_WV_STATUS_BAR);
        if (shouldHandleStatusBarMount !== statusBarHandling) {
            setStatusBarHandling(shouldHandleStatusBarMount);
        }
        const handleStorageChange = () => {
            const shouldHandleStatusBar = asyncStorageApi.restoreDataNoLink(KEY_WV_STATUS_BAR);
            console.log({ shouldHandleStatusBar });
            setStatusBarHandling(shouldHandleStatusBar);
        };

        // Attach the event listener when the component mounts
        window.addEventListener('storage', handleStorageChange);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redirect to home if not admin
    if (isAdmin && !isUserAdmin()) {
        return <Navigate to={paths.home} replace />;
    }

    const Title = isMobileSize ? Text.H2 : Text.H0;
    const formatedTitle = (!isWebview && !isMobileSize) || action ? title : '';

    return (
        <div className={clsx(classes.contentContainerRoot, 'LCcontentContainerRoot')}>
            {aboveAll && (
                <div className={clsx(classes.drawerContainer, isAdmin && classes.drawerContainerAdmin)}>
                    {!isAdmin && <DrawerMenu noAdmin />}
                    {isAdmin && <DrawerAdminMenu />}
                </div>
            )}
            <div
                className={clsx(
                    classes.pageContainer,
                    isTabletSize && classes.pageContainerMarginResponsive,
                    isWebview && statusBarHandling && classes.pageContainerMarginStatusBar,
                    bottomBarHeight > 0 && classes.pageContainerNavBar,
                    shouldReduceMargin && classes.pageContainerResponsive,
                    isAdmin && classes.pageContainerAdmin,
                )}
            >
                {title && (
                    <div
                        className={clsx(
                            globalClasses.flexRow,
                            classes.headerTitleShared,
                            isMobileSize && !action && classes.headerTitle,
                            isWebview && classes.statusBarMargin,
                        )}
                    >
                        <div className={clsx(actionLeft && classes.actionLeft)}>
                            <Title text={formatedTitle} />
                            {actionLeft}
                        </div>
                        {action}
                    </div>
                )}
                {children}
            </div>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        headerTitle: {
            zIndex: 3,
            display: 'flex',
            width: 'fit-content',
            marginLeft: 40,
            position: 'fixed',
            top: 12,
        },
        headerTitleShared: {
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        statusBarMargin: {
            top: '100px!important',
        },
        contentContainerRoot: {
            display: 'flex',
            marginTop: NavTopBarHeight,
        },
        noMargin: {
            marginTop: '0px!important',
        },
        drawerContainer: {
            position: 'fixed',
            paddingLeft: 12,
            paddingTop: ContentPaddingTop - 20,
            height: `calc(100% - ${NavTopBarHeight}px)`,
            width: NavSideBarWidth,
            borderRight: `1px solid ${theme.palette.colors.background.menuSeparator}`,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
        },
        drawerContainerAdmin: {
            zIndex: 2,
            background: theme.palette.colors.admin,
        },
        pageContainer: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 580,
            margin: 25,
            marginTop: ContentPaddingTop,
            marginLeft: NavSideBarWidth + 110,
        },
        pageContainerResponsive: {
            marginLeft: NavSideBarWidth + 40,
        },
        pageContainerMarginResponsive: {
            margin: 'auto',
            marginTop: 16,
            marginBottom: 30,
            paddingLeft: 20,
            paddingRight: 20,
            maxWidth: '100%',
            // alignItems: 'center',
        },
        pageContainerMarginStatusBar: {
            marginTop: (props: StyleProps) => `${props.statusBarHeight}px`,
        },
        // The Android WebView goes edge to edge, so the content has to step back from the bottom
        // system bar the same way it steps back from the status bar above.
        pageContainerNavBar: {
            paddingBottom: (props: StyleProps) => `${props.bottomBarHeight}px`,
        },
        pageContainerAdmin: {
            maxWidth: 'unset!important',
            paddingRight: 30,
        },
        actionLeft: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 20,
        },
    }),
);

const mapStateToProps = (state: any): StateProps => ({
    contract: adminSelectors.contract(state),
    contractPlan: adminSelectors.contractPlan(state),
});

const mapDispatchToProps: DispatchProps = {
    setContract: adminActions.setContract,
    setContractPlan: adminActions.setContractPlan,
};

export default connect<StateProps, DispatchProps, ContentContainerProps>(
    mapStateToProps,
    mapDispatchToProps,
)(ContentContainer);
